import { Box, Radio, Stack, Typography } from "@mui/material";
import { ActionButton, SelectableOption } from "@zfp-akademie-a-s/zfp-design";
import { DeleteIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { ChildIcon } from "Components/_Shared/Icons/Icons";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { differenceInYears } from "date-fns";

type Props = {
  readonly onDelete?: () => void;
  readonly clientId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly disabled?: boolean;
  readonly birthDate?: Date;
};

// One child in the goal's RadioGroup: the package's option, its Radio drawn as the child icon. The delete
// button sits over the option's end but outside its label, so deleting a child doesn't choose it.
export const ChildTile: React.FC<Props> = ({ clientId, firstName, lastName, birthDate, disabled, onDelete }) => {
  const { t } = useResource();
  const canDelete = !disabled && !!onDelete;

  const renderAge = (birthDate: Date) => {
    const age = differenceInYears(new Date(), birthDate);

    if (age === 1) {
      return `${age} ${t(Resources.Common.Year)}`;
    }

    if (age <= 4) {
      return `${age} ${t(Resources.Common.YearsFew)}`;
    }

    return `${age} ${t(Resources.Common.Years)}`;
  };

  return (
    <Box position="relative">
      <SelectableOption
        value={clientId}
        disabled={disabled}
        control={<Radio icon={<ChildIcon />} checkedIcon={<ChildIcon />} />}
        label={
          <Stack>
            <Typography variant="body2" fontWeight={600}>
              {firstName} {lastName}
            </Typography>
            <Typography variant="caption" color="secondary">
              {birthDate ? renderAge(birthDate) : t(Resources.AnalysisDetail.Goals.PlannedChild)}
            </Typography>
          </Stack>
        }
        // the delete button covers the end of the box, so the label keeps clear of it
        sx={{ borderColor: "divider", py: 3, pr: canDelete ? 12 : 3 }}
      />
      {canDelete && (
        <Box position="absolute" top="50%" right={12} sx={{ transform: "translateY(-50%)" }}>
          <ActionButton label={t(Resources.Common.Delete)} icon={<DeleteIcon />} tone="danger" onClick={onDelete} />
        </Box>
      )}
    </Box>
  );
};
